import React, { useState } from 'react';

const COLORS = [
  '#7c6ef8', '#06b6d4', '#10b981', '#f59e0b',
  '#ef4444', '#3b82f6', '#a855f7', '#ec4899',
  '#14b8a6', '#f97316',
];

const fmt = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (isNaN(n)) return String(v);
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `${(n / 1_000).toFixed(1)}K`;
  if (n % 1 !== 0)    return n.toFixed(1);
  return n.toLocaleString();
};

// Smooth bezier path through points
function smoothPath(pts) {
  if (pts.length < 2) return '';
  let d = `M ${pts[0][0]} ${pts[0][1]}`;
  for (let i = 1; i < pts.length; i++) {
    const cp1x = pts[i - 1][0] + (pts[i][0] - pts[i - 1][0]) * 0.4;
    const cp1y = pts[i - 1][1];
    const cp2x = pts[i][0] - (pts[i][0] - pts[i - 1][0]) * 0.4;
    const cp2y = pts[i][1];
    d += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${pts[i][0]} ${pts[i][1]}`;
  }
  return d;
}

// ── Line Chart ─────────────────────────────────────────────────────────────
function LineChart({ data, series, title, yLabel }) {
  const [tooltip, setTooltip] = useState(null);
  const W = 560, H = 300;
  const pad = { top: 24, right: 140, bottom: 80, left: 56 };
  const pw = W - pad.left - pad.right;
  const ph = H - pad.top - pad.bottom;

  const allVals = data.flatMap(d => series.map(s => Number(d[s]) || 0));
  const maxV = Math.max(...allVals) * 1.12 || 1;
  const minV = 0;

  const xS = (i) => (i / Math.max(data.length - 1, 1)) * pw;
  const yS = (v) => ph - ((v - minV) / (maxV - minV)) * ph;

  const gridLines = [0, 0.25, 0.5, 0.75, 1];

  return (
    <div className="chart-wrap">
      {title && <div className="chart-title">{title}</div>}
      <div className="chart-svg-scroll">
      <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} className="chart-svg">
        <defs>
          {series.map((s, i) => (
            <linearGradient key={s} id={`lg-${i}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={COLORS[i % COLORS.length]} stopOpacity="0.22" />
              <stop offset="100%" stopColor={COLORS[i % COLORS.length]} stopOpacity="0.01" />
            </linearGradient>
          ))}
          <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.12" />
          </filter>
        </defs>

        <g transform={`translate(${pad.left},${pad.top})`}>
          {/* Grid */}
          {gridLines.map(t => {
            const y = t * ph;
            const val = maxV * (1 - t);
            return (
              <g key={t}>
                <line x1={0} y1={y} x2={pw} y2={y}
                  stroke={t === 1 ? '#c4cad6' : '#e8eaf0'}
                  strokeWidth={t === 1 ? 1.5 : 1}
                  strokeDasharray={t === 0 ? '0' : '5,4'} />
                <text x={-10} y={y + 4} textAnchor="end" fontSize="10.5" fill="#9ca3af">
                  {fmt(val)}
                </text>
              </g>
            );
          })}

          {/* Y label */}
          {yLabel && (
            <text x={-40} y={ph / 2} textAnchor="middle" fontSize="10" fill="#9ca3af"
              transform={`rotate(-90, -40, ${ph / 2})`}>
              {yLabel}
            </text>
          )}

          {/* X labels — rotated when many points, skip labels to avoid overlap */}
          {(() => {
            const maxLabels = 10;
            const stride = Math.max(1, Math.ceil(data.length / maxLabels));
            const rotate = data.length > 6;
            return data.map((d, i) => {
              if (i % stride !== 0 && i !== data.length - 1) return null;
              const lbl = String(d.label);
              return rotate ? (
                <text key={i} x={xS(i)} y={ph + 10} textAnchor="end" fontSize="10" fill="#6b7280"
                  transform={`rotate(-42, ${xS(i)}, ${ph + 10})`}>
                  {lbl.length > 20 ? lbl.slice(0, 18) + '…' : lbl}
                </text>
              ) : (
                <text key={i} x={xS(i)} y={ph + 18} textAnchor="middle" fontSize="11" fill="#6b7280">
                  {lbl.length > 12 ? lbl.slice(0, 10) + '…' : lbl}
                </text>
              );
            });
          })()}

          {/* Areas + lines */}
          {series.map((s, si) => {
            const pts = data.map((d, i) => [xS(i), yS(Number(d[s]) || 0)]);
            const linePath = smoothPath(pts);
            const areaPath = linePath +
              ` L ${pts[pts.length - 1][0]} ${ph} L ${pts[0][0]} ${ph} Z`;
            return (
              <g key={s}>
                <path d={areaPath} fill={`url(#lg-${si})`} />
                <path d={linePath} fill="none" stroke={COLORS[si % COLORS.length]}
                  strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                  filter="url(#shadow)" />
              </g>
            );
          })}

          {/* Dots + end labels */}
          {series.map((s, si) => {
            const color = COLORS[si % COLORS.length];
            return data.map((d, i) => {
              const x = xS(i), y = yS(Number(d[s]) || 0);
              const isLast = i === data.length - 1;
              return (
                <g key={`${s}-${i}`}>
                  <circle cx={x} cy={y} r="8" fill={color} opacity="0"
                    style={{ cursor: 'pointer' }}
                    onMouseEnter={() => setTooltip({ x: x + pad.left, y: y + pad.top, s, d, si })}
                    onMouseLeave={() => setTooltip(null)} />
                  <circle cx={x} cy={y} r="4" fill={color}
                    stroke="white" strokeWidth="1.5" />
                  {isLast && (
                    <g>
                      <rect x={x + 10} y={y - 9} width={
                        Math.max((s.length <= 3 ? s.toUpperCase() : s).length * 6.5 + fmt(d[s]).length * 6.5 + 14, 40)
                      } height={18} rx="4" fill={color} opacity="0.12" />
                      <text x={x + 17} y={y + 3.5} fontSize="10.5" fill={color} fontWeight="700">
                        {s.length <= 3 ? s.toUpperCase() : s} · {fmt(d[s])}
                      </text>
                    </g>
                  )}
                </g>
              );
            });
          })}

          {/* Tooltip */}
          {tooltip && (() => {
            const tx = tooltip.x - pad.left, ty = tooltip.y - pad.top;
            const val = tooltip.d[tooltip.s];
            const lbl = `${tooltip.s}: ${fmt(val)}`;
            return (
              <g>
                <rect x={tx + 12} y={ty - 18} rx="5" ry="5" width={lbl.length * 6.8 + 16} height={24}
                  fill="#1e2235" opacity="0.9" />
                <text x={tx + 20} y={ty - 1} fontSize="11" fill="#fff" fontWeight="600">{lbl}</text>
              </g>
            );
          })()}
        </g>
      </svg>
      </div>

      {/* Legend */}
      <div className="chart-legend">
        {series.map((s, i) => (
          <div key={s} className="chart-legend-item">
            <span className="chart-legend-dot" style={{ background: COLORS[i % COLORS.length] }} />
            <span>{s.length <= 3 ? s.toUpperCase() : s}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Bar Chart ──────────────────────────────────────────────────────────────
function BarChart({ data, series, title, yLabel }) {
  const [tooltip, setTooltip] = useState(null);
  const W = 560, H = 300;
  const pad = { top: 24, right: 20, bottom: 88, left: 56 };
  const pw = W - pad.left - pad.right;
  const ph = H - pad.top - pad.bottom;

  const allVals = data.flatMap(d => series.map(s => Number(d[s]) || 0));
  const maxV = Math.max(...allVals) * 1.15 || 1;

  const groupW = pw / data.length;
  const barW   = Math.min((groupW / series.length) * 0.72, 40);
  const groupPad = (groupW - barW * series.length) / 2;

  const yS = (v) => ph - (v / maxV) * ph;

  const gridLines = [0, 0.25, 0.5, 0.75, 1];

  return (
    <div className="chart-wrap">
      {title && <div className="chart-title">{title}</div>}
      <div className="chart-svg-scroll">
      <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} className="chart-svg">
        <defs>
          {series.map((s, i) => (
            <linearGradient key={s} id={`bg-${i}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={COLORS[i % COLORS.length]} stopOpacity="1" />
              <stop offset="100%" stopColor={COLORS[i % COLORS.length]} stopOpacity="0.65" />
            </linearGradient>
          ))}
          <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="2" stdDeviation="2" floodOpacity="0.1" />
          </filter>
        </defs>

        <g transform={`translate(${pad.left},${pad.top})`}>
          {gridLines.map(t => {
            const y = t * ph;
            return (
              <g key={t}>
                <line x1={0} y1={y} x2={pw} y2={y}
                  stroke={t === 1 ? '#c4cad6' : '#e8eaf0'}
                  strokeWidth={t === 1 ? 1.5 : 1}
                  strokeDasharray={t === 0 ? '0' : '5,4'} />
                <text x={-10} y={y + 4} textAnchor="end" fontSize="10.5" fill="#9ca3af">
                  {fmt(maxV * (1 - t))}
                </text>
              </g>
            );
          })}

          {data.map((d, di) => {
            const gx = di * groupW;
            const labelStr = String(d.label);
            const maxLabels = 10;
            const labelStride = Math.max(1, Math.ceil(data.length / maxLabels));
            const showLabel = di % labelStride === 0 || di === data.length - 1;
            return (
              <g key={di}>
                {/* X label — rotated to prevent overlap, skipped on dense axes */}
                {showLabel && (
                  <text
                    x={gx + groupW / 2}
                    y={ph + 10}
                    textAnchor="end"
                    fontSize="10"
                    fill="#6b7280"
                    transform={`rotate(-42, ${gx + groupW / 2}, ${ph + 10})`}
                  >
                    {labelStr.length > 20 ? labelStr.slice(0, 18) + '…' : labelStr}
                  </text>
                )}

                {series.map((s, si) => {
                  const val = Number(d[s]) || 0;
                  const bh  = Math.max((val / maxV) * ph, 2);
                  const bx  = gx + groupPad + si * barW;
                  const by  = yS(val);
                  const r   = Math.min(4, barW / 3);
                  return (
                    <g key={s}>
                      {/* Rounded top bar */}
                      <path
                        d={`M ${bx + r} ${by}
                            H ${bx + barW - r}
                            Q ${bx + barW} ${by} ${bx + barW} ${by + r}
                            V ${ph}
                            H ${bx}
                            V ${by + r}
                            Q ${bx} ${by} ${bx + r} ${by}`}
                        fill={`url(#bg-${si})`}
                        filter="url(#shadow)"
                        style={{ cursor: 'pointer' }}
                        onMouseEnter={() => setTooltip({ bx, by, s, val, si })}
                        onMouseLeave={() => setTooltip(null)}
                      />
                      {/* Value on top */}
                      {bh > 22 && (
                        <text x={bx + barW / 2} y={by - 5} textAnchor="middle"
                          fontSize="9.5" fill={COLORS[si % COLORS.length]} fontWeight="700">
                          {fmt(val)}
                        </text>
                      )}
                    </g>
                  );
                })}
              </g>
            );
          })}

          {/* Tooltip */}
          {tooltip && (() => {
            const lbl = `${tooltip.s}: ${fmt(tooltip.val)}`;
            return (
              <g>
                <rect x={tooltip.bx - 4} y={tooltip.by - 26} rx="5" width={lbl.length * 7 + 12} height={22}
                  fill="#1e2235" opacity="0.92" />
                <text x={tooltip.bx + 2} y={tooltip.by - 10} fontSize="11" fill="#fff" fontWeight="600">
                  {lbl}
                </text>
              </g>
            );
          })()}
        </g>
      </svg>
      </div>

      {series.length > 1 && (
        <div className="chart-legend">
          {series.map((s, i) => (
            <div key={s} className="chart-legend-item">
              <span className="chart-legend-dot" style={{ background: COLORS[i % COLORS.length] }} />
              <span>{s}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Pie / Donut Chart ──────────────────────────────────────────────────────
function PieChart({ data, title }) {
  const [hovered, setHovered] = useState(null);
  const W = 420, H = 260;
  const cx = 150, cy = 130, R = 100, r = 52;
  const total = data.reduce((s, d) => s + (Number(d.value) || 0), 0) || 1;

  let angle = -Math.PI / 2;
  const slices = data.map((d, i) => {
    const val   = Number(d.value) || 0;
    const sweep = (val / total) * 2 * Math.PI;
    const start = angle;
    angle += sweep;
    const mid   = start + sweep / 2;
    const large = sweep > Math.PI ? 1 : 0;
    const x1 = cx + R * Math.cos(start), y1 = cy + R * Math.sin(start);
    const x2 = cx + R * Math.cos(start + sweep), y2 = cy + R * Math.sin(start + sweep);
    const ix1 = cx + r * Math.cos(start), iy1 = cy + r * Math.sin(start);
    const ix2 = cx + r * Math.cos(start + sweep), iy2 = cy + r * Math.sin(start + sweep);
    const pct  = ((val / total) * 100).toFixed(1);
    return { ...d, val, sweep, start, mid, large, x1, y1, x2, y2, ix1, iy1, ix2, iy2, pct, i };
  });

  return (
    <div className="chart-wrap">
      {title && <div className="chart-title">{title}</div>}
      <div className="chart-svg-scroll">
      <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} className="chart-svg">
        <defs>
          <filter id="pie-shadow">
            <feDropShadow dx="0" dy="3" stdDeviation="4" floodOpacity="0.15" />
          </filter>
        </defs>

        {slices.map((s, i) => {
          const isHov = hovered === i;
          const expand = isHov ? 8 : 0;
          const midX = cx + (R + expand) * Math.cos(s.mid);
          const midY = cy + (R + expand) * Math.sin(s.mid);
          const ox = (midX - cx) * 0.08, oy = (midY - cy) * 0.08;
          return (
            <path
              key={i}
              d={`M ${s.ix1} ${s.iy1}
                  L ${s.x1 + ox} ${s.y1 + oy}
                  A ${R} ${R} 0 ${s.large} 1 ${s.x2 + ox} ${s.y2 + oy}
                  L ${s.ix2} ${s.iy2}
                  A ${r} ${r} 0 ${s.large} 0 ${s.ix1} ${s.iy1} Z`}
              fill={COLORS[i % COLORS.length]}
              opacity={isHov ? 1 : 0.88}
              filter="url(#pie-shadow)"
              style={{ cursor: 'pointer', transition: 'opacity 0.15s' }}
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
            />
          );
        })}

        {/* Centre */}
        <circle cx={cx} cy={cy} r={r - 2} fill="white" />
        {hovered === null && (
          <>
            <text x={cx} y={cy - 6} textAnchor="middle" fontSize="11" fill="#9ca3af">Total</text>
            <text x={cx} y={cy + 12} textAnchor="middle" fontSize="15" fontWeight="700" fill="#1e2235">
              {fmt(total)}
            </text>
          </>
        )}

        {/* Hover label — replaces (not overlays) the centre Total/value text above */}
        {hovered !== null && (() => {
          const s = slices[hovered];
          return (
            <g>
              <text x={cx} y={cy - 6} textAnchor="middle" fontSize="11" fill={COLORS[hovered % COLORS.length]} fontWeight="600">
                {s.label}
              </text>
              <text x={cx} y={cy + 12} textAnchor="middle" fontSize="15" fontWeight="800" fill="#1e2235">
                {s.pct}%
              </text>
              <text x={cx} y={cy + 27} textAnchor="middle" fontSize="10" fill="#6b7280">
                {fmt(s.val)}
              </text>
            </g>
          );
        })()}

        {/* Legend — right side */}
        {slices.map((s, i) => {
          const ly = 20 + i * 22;
          return (
            <g key={i} style={{ cursor: 'pointer' }}
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}>
              <rect x={280} y={ly} width={12} height={12} rx="3"
                fill={COLORS[i % COLORS.length]} opacity={hovered === i ? 1 : 0.8} />
              <text x={298} y={ly + 10} fontSize="11"
                fill={hovered === i ? '#1e2235' : '#4b5563'} fontWeight={hovered === i ? '600' : '400'}>
                {String(s.label).length > 16 ? String(s.label).slice(0, 14) + '…' : s.label}
              </text>
              <text x={420} y={ly + 10} textAnchor="end" fontSize="10.5" fill="#9ca3af">
                {s.pct}%
              </text>
            </g>
          );
        })}
      </svg>
      </div>
    </div>
  );
}

// ── Chart spec validator (M-4) ─────────────────────────────────────────────
// Catches structural errors that pass JSON.parse but would cause ChartRenderer
// to silently render blank or crash (wrong type, missing data array, series
// keys that don't exist in data items).
function validateChartSpec(spec) {
  if (!spec || typeof spec !== 'object') {
    return { valid: false, reason: 'Chart spec is not an object.' };
  }
  const VALID_TYPES = ['line', 'bar', 'pie'];
  if (!spec.type || !VALID_TYPES.includes(spec.type)) {
    return { valid: false, reason: `Unknown chart type "${spec.type}". Expected: line, bar, or pie.` };
  }
  if (!Array.isArray(spec.data) || spec.data.length === 0) {
    return { valid: false, reason: 'Chart "data" must be a non-empty array.' };
  }
  const first = spec.data[0];
  if (spec.type === 'pie') {
    if (!('value' in first)) {
      return { valid: false, reason: 'Pie chart data items must have a "value" field.' };
    }
  } else {
    if (!('label' in first)) {
      return { valid: false, reason: 'Chart data items must have a "label" field.' };
    }
    const seriesKeys = spec.series || ['value'];
    const missing = seriesKeys.filter(k => !(k in first));
    if (missing.length > 0) {
      return { valid: false, reason: `Series key(s) not found in data: ${missing.join(', ')}.` };
    }
  }
  return { valid: true };
}

// ── Main export ────────────────────────────────────────────────────────────
export default function ChartRenderer({ spec }) {
  const validation = validateChartSpec(spec);
  if (!validation.valid) {
    return <div className="chart-error">Invalid chart: {validation.reason}</div>;
  }

  const { type, title, data, series, y_label, x_label } = spec;

  // Normalise: if no series key, assume single-series with key "value"
  const seriesKeys = series || ['value'];

  if (type === 'pie') {
    return <PieChart data={data} title={title} />;
  }
  if (type === 'bar') {
    return <BarChart data={data} series={seriesKeys} title={title} yLabel={y_label} />;
  }
  // Default: line
  return <LineChart data={data} series={seriesKeys} title={title} yLabel={y_label} />;
}
